import { useEffect, useRef, useState } from 'react';

/**
 * Hook simple para animaciones "aparece al hacer scroll".
 * Devuelve un ref para poner en el elemento a observar y un booleano
 * que pasa a true la primera vez que el elemento entra en el viewport
 * (una sola vez: no se vuelve a ocultar al scrollear para arriba/abajo).
 *
 * No depende de ninguna librería externa, usa la Intersection Observer
 * API nativa del navegador.
 *
 * @param {Object} options
 * @param {number} options.threshold - % del elemento visible para disparar (0 a 1)
 * @param {string} options.rootMargin - margen del viewport, igual que en IntersectionObserver
 */
export default function useInView({ threshold = 0.15, rootMargin = '0px' } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Respeta la preferencia de "reducir movimiento" del sistema operativo:
    // si el usuario la activó, mostramos todo directo, sin animación.
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(el);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return [ref, inView];
}
